import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Bug, ChevronDown, Info, ListChecks, Rocket, Sparkles, Wrench,
} from 'lucide-react';
import { AdminEmpty, AdminError, AdminSkeleton } from './components/PageState';
import {
  groupUpdatesByDay, loadAdminUpdates, markUpdatesSeen, readSeenDate,
} from './lib/updates';
import './updates.css';

// Madde türleri: rozetin rengi ve adı tek yerdedir, hem süzgeç kutuları hem
// gün rozetleri hem satırlar buradan okur. Tanınmayan tür backend'de "note"a
// düşer.
const KINDS = {
  new: { label: 'Yeni', icon: Sparkles, tone: 'new' },
  improve: { label: 'İyileştirme', icon: Wrench, tone: 'improve' },
  fix: { label: 'Düzeltme', icon: Bug, tone: 'fix' },
  note: { label: 'Bilgi', icon: Info, tone: 'note' },
};

const KIND_ORDER = ['new', 'improve', 'fix', 'note'];

// Ay ve gün adları sabittir: `toLocaleDateString` tarayıcının ICU verisine
// bağlıdır ve "Eki" gibi bir kısaltmayı İngilizce büyük harfe çevirdiğinde
// (EKI) Türkçe'yi bozar.
const MONTHS = [
  'Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz',
  'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara',
];
const MONTHS_LONG = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];
const WEEKDAYS = [
  'Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi',
];

const parseDay = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Takvim yaprağı ve başlık için gün bilgisi; tarih bozuksa ham değer kalır. */
const dayLabels = (value) => {
  const date = parseDay(value);
  if (!date) {
    return {
      number: '—', month: '', title: value || '', weekday: '',
    };
  }
  return {
    number: String(date.getDate()).padStart(2, '0'),
    month: MONTHS[date.getMonth()],
    title: `${date.getDate()} ${MONTHS_LONG[date.getMonth()]} ${date.getFullYear()}`,
    weekday: WEEKDAYS[date.getDay()],
  };
};

const todayKey = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const Updates = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [kind, setKind] = useState('');
  // Kapalı günlerin tarihleri. Varsayılan "açık" olduğu için kapalılar
  // tutulur: liste sonradan büyüdüğünde yeni gün kendiliğinden açık gelir.
  const [closed, setClosed] = useState(() => new Set());

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    loadAdminUpdates()
      .then((data) => {
        // Okundu işareti aşağıda güncellenir; hangi günün kapalı geleceğine
        // karar vermek için **önceki** değer okunmalıdır.
        const seen = readSeenDate();
        setEntries(data.entries);
        // Okunmuş günler kapalı gelir: yönetici zaten gördüğü bir günü ikinci
        // kez okumaz, kutunun başlığı ne olduğunu zaten söyler. Hiç
        // okunmamışsa (ilk açılış) her gün açıktır.
        setClosed(new Set(
          seen
            ? (data.entries || [])
              .map((entry) => String(entry?.date || ''))
              .filter((date) => date && date <= seen)
            : [],
        ));
        // Sayfa açıldığı an okundu sayılır: yönetici listeyi görmüştür,
        // rozetin ikinci bir tıklama beklemesi onu kalıcı yapardı.
        markUpdatesSeen(data.latest_date);
      })
      .catch((requestError) => {
        setError(
          requestError.response?.data?.detail
          || 'Yenilikler listesi alınamadı',
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Süzgeç yalnızca dolu türleri sunar: hiç düzeltme yoksa "Düzeltme" kutusu
  // her zaman boş bir liste veren ölü bir düğme olurdu.
  const counts = useMemo(() => {
    const totals = {};
    entries.forEach((entry) => {
      (entry.changes || []).forEach((change) => {
        totals[change.kind] = (totals[change.kind] || 0) + 1;
      });
    });
    return totals;
  }, [entries]);

  const total = useMemo(
    () => Object.values(counts).reduce((sum, value) => sum + value, 0),
    [counts],
  );

  const days = useMemo(() => groupUpdatesByDay(
    entries
      .map((entry) => ({
        ...entry,
        changes: (entry.changes || []).filter(
          (change) => !kind || change.kind === kind,
        ),
      }))
      .filter((entry) => entry.changes.length > 0),
  ), [entries, kind]);

  const today = todayKey();
  const newestDate = days.length ? days[0].date : '';
  // Süzgeç açıkken kutular kapanmaz: yönetici bir türü seçtiğinde sonucu
  // görmek için ayrıca her günü tıklamak zorunda kalırdı.
  const allOpen = Boolean(kind) || closed.size === 0;

  const toggleDay = (date) => setClosed((previous) => {
    const next = new Set(previous);
    if (next.has(date)) next.delete(date);
    else next.add(date);
    return next;
  });

  const toggleAll = () => setClosed((previous) => (
    previous.size === 0 ? new Set(days.map((day) => day.date)) : new Set()
  ));

  return (
    <div className="upd-page">
      <header className="upd-hero">
        <span className="upd-hero-mark"><Rocket size={23} /></span>
        <div className="upd-hero-copy">
          <h1>Yenilikler</h1>
          <p>
            Sisteme eklenen özellikler, değişen davranışlar ve düzeltilen
            hatalar — her gün kendi kutusunda, en yenisi en üstte.
          </p>
        </div>
        {!loading && !error && days.length > 0 && (
          <span className="upd-hero-count">
            <ListChecks size={15} />
            {days.length} gün · {total} madde
          </span>
        )}
      </header>

      {loading && <AdminSkeleton rows={5} label="Yenilikler yükleniyor" />}
      {!loading && error && <AdminError message={error} onRetry={load} />}

      {!loading && !error && (
        <>
          <div className="upd-filters">
            <button
              type="button"
              className={`upd-metric${kind ? '' : ' is-active'}`}
              onClick={() => setKind('')}
            >
              <span className="upd-metric-icon tone-all"><ListChecks size={16} /></span>
              <span className="upd-metric-copy">
                <strong>{total}</strong>
                <span>Tümü</span>
              </span>
            </button>
            {KIND_ORDER.filter((key) => counts[key]).map((key) => {
              const meta = KINDS[key];
              const Icon = meta.icon;
              return (
                <button
                  key={key}
                  type="button"
                  className={`upd-metric${kind === key ? ' is-active' : ''}`}
                  onClick={() => setKind(kind === key ? '' : key)}
                >
                  <span className={`upd-metric-icon tone-${meta.tone}`}>
                    <Icon size={16} />
                  </span>
                  <span className="upd-metric-copy">
                    <strong>{counts[key]}</strong>
                    <span>{meta.label}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {days.length > 1 && !kind && (
            <div className="upd-toolbar">
              <button type="button" className="upd-toolbar-btn" onClick={toggleAll}>
                <ChevronDown size={14} className={allOpen ? 'is-flipped' : ''} />
                {closed.size === 0 ? 'Tüm günleri kapat' : 'Tüm günleri aç'}
              </button>
            </div>
          )}

          {days.length === 0 && (
            <AdminEmpty
              title="Kayıtlı bir yenilik yok"
              description="Bu süzgece uyan bir madde bulunamadı."
            />
          )}

          <div className="upd-timeline">
            {days.map((day) => {
              const label = dayLabels(day.date);
              const open = allOpen || !closed.has(day.date);
              const isToday = day.date === today;
              const isNewest = day.date === newestDate;
              return (
                <section
                  className={`upd-day${open ? ' is-open' : ''}${isNewest ? ' is-newest' : ''}`}
                  key={day.date}
                >
                  <button
                    type="button"
                    className="upd-day-head"
                    onClick={() => toggleDay(day.date)}
                    aria-expanded={open}
                  >
                    <span className="upd-day-mark">
                      <strong>{label.number}</strong>
                      <span>{label.month}</span>
                    </span>
                    <span className="upd-day-copy">
                      <span className="upd-day-title">
                        {label.title}
                        {isToday && <em className="upd-day-flag">Bugün</em>}
                        {!isToday && isNewest && (
                          <em className="upd-day-flag">En yeni</em>
                        )}
                      </span>
                      <span className="upd-day-meta">
                        {label.weekday}
                        {label.weekday && ' · '}
                        {day.total} madde
                        {day.entries.length > 1 && ` · ${day.entries.length} çalışma`}
                      </span>
                    </span>
                    {/* Kutu kapalıyken de bilgi taşır: o gün hangi türden kaç
                        madde var, başlıktan okunur. */}
                    <span className="upd-day-tally">
                      {KIND_ORDER.filter((key) => day.counts[key]).map((key) => {
                        const meta = KINDS[key];
                        const Icon = meta.icon;
                        return (
                          <span
                            key={key}
                            className={`upd-tally tone-${meta.tone}`}
                            title={`${meta.label}: ${day.counts[key]}`}
                          >
                            <Icon size={11} aria-hidden="true" />
                            {day.counts[key]}
                          </span>
                        );
                      })}
                    </span>
                    <ChevronDown size={17} className="upd-day-caret" />
                  </button>

                  {open && (
                    <div className="upd-day-body">
                      {day.entries.map((entry, entryIndex) => (
                        // Kayıtların kendi kimliği yok; aynı güne birden çok
                        // kayıt düştüğü için anahtar tarih + sıradır.
                        // eslint-disable-next-line react/no-array-index-key
                        <article className="upd-entry" key={`${day.date}-${entryIndex}`}>
                          {entry.title && (
                            <h2 className="upd-entry-title">{entry.title}</h2>
                          )}
                          {entry.summary && (
                            <p className="upd-entry-summary">{entry.summary}</p>
                          )}
                          <ul className="upd-changes">
                            {entry.changes.map((change, index) => {
                              const meta = KINDS[change.kind] || KINDS.note;
                              const Icon = meta.icon;
                              return (
                                // eslint-disable-next-line react/no-array-index-key
                                <li key={`${day.date}-${entryIndex}-${index}`}>
                                  <span className={`upd-kind tone-${meta.tone}`}>
                                    <Icon size={12} aria-hidden="true" />
                                    {meta.label}
                                  </span>
                                  <div className="upd-change-body">
                                    <p>{change.text}</p>
                                    {change.where && (
                                      <span className="upd-where">{change.where}</span>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Updates;
